/**
 * CameraPage Component - Hedita Enterprise
 * Full page for creating/editing cameras (converted from drawer)
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BedDouble, ArrowLeft, CheckCircle, AlertCircle, Save } from "lucide-react";
import { useCreateCamera, useCamera, useUpdateCamera } from "@/features/strutture/hooks";
import { useQueryClient } from "@tanstack/react-query";

const TIPI_CAMERA = ["Singola", "Doppia", "Matrimoniale", "Tripla", "Quadrupla", "Suite", "Appartamento", "Altro"];
const STATI_CAMERA = ["Disponibile", "Occupata", "In pulizia", "Non disponibile"];

const empty = {
  nome: "",
  codice: "",
  tipo: "Singola",
  piano: "",
  posti: 1,
  stato: "Disponibile",
  prezzo_base: 0,
  prezzo_weekend: 0,
  note: "",
};

type FormData = typeof empty;

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-3 rounded-lg border border-border bg-card text-base focus:outline-none focus:border-primary/50";
const textareaCls = `${inputCls} resize-none`;

function Input({ value, onChange, type = "text", placeholder }: { value: any; onChange: (v: any) => void; type?: string; placeholder?: string }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder || ""}
      onChange={e => onChange(type === "number" ? (e.target.value ? Number(e.target.value) : 0) : e.target.value)}
      className={inputCls}
      style={{ fontSize: "16px" }}
    />
  );
}

function Textarea({ value, onChange, rows = 4, placeholder }: { value: any; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return (
    <textarea
      value={value ?? ""}
      placeholder={placeholder || ""}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      className={textareaCls}
      style={{ fontSize: "16px" }}
    />
  );
}

function Select({ value, onChange, options }: { value: any; onChange: (v: string) => void; options: string[] }) {
  return (
    <select
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      className={inputCls + " appearance-none"}
      style={{ fontSize: "16px" }}
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function CameraPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  
  const { data: camera, isLoading } = useCamera(id || "");
  const createCamera = useCreateCamera();
  const updateCamera = useUpdateCamera();
  const qc = useQueryClient();

  const [form, setForm] = useState(empty);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (camera) {
      setForm({
        nome: camera.nome || "",
        codice: camera.codice || "",
        tipo: camera.tipo || "Singola",
        piano: camera.piano || "",
        posti: camera.posti || 1,
        stato: camera.stato || "Disponibile",
        prezzo_base: camera.prezzo_base || 0,
        prezzo_weekend: camera.prezzo_weekend || 0,
        note: camera.note || "",
      });
    } else if (isNew) {
      setForm(empty);
    }
  }, [camera, isNew]);

  const set = (k: keyof FormData, v: any) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.nome.trim()) return;
    setSaveStatus("saving");
    setSaveError(null);

    const onSuccess = () => {
      qc.invalidateQueries({ queryKey: ["camere"] });
      setSaveStatus("saved");
      if (isNew) {
        setTimeout(() => navigate("/camere"), 1500);
      } else {
        setTimeout(() => setSaveStatus("idle"), 2500);
      }
    };

    const onError = (err: any) => {
      setSaveStatus("error");
      setSaveError(err?.message ?? "Errore durante il salvataggio. Riprova.");
    };

    if (isNew) {
      createCamera.mutate(form, { onSuccess, onError });
    } else {
      updateCamera.mutate({ id: id!, data: form }, { onSuccess, onError });
    }
  };

  const handleBack = () => {
    navigate("/camere");
  };

  if (isLoading && !isNew) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={handleBack} className="p-2 hover:bg-muted rounded-lg transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="h-8 bg-muted rounded animate-pulse w-48" />
          </div>
          <div className="bg-card rounded-2xl shadow-sm p-6 space-y-6">
            <div className="h-16 bg-muted rounded animate-pulse" />
            <div className="h-16 bg-muted rounded animate-pulse" />
            <div className="h-16 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleBack}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
                <BedDouble className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <BedDouble className="h-5 w-5 text-primary" />
                  {isNew ? "Nuova Camera" : "Modifica Camera"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isNew ? "Aggiungi una nuova camera al sistema" : "Modifica i dati della camera"}
                </p>
              </div>
            </div>
          </div>
          
          {saveStatus === "saved" && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Salvato!</span>
            </div>
          )}
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="p-6 space-y-6">
            {/* Nome e Codice */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Nome Camera" required>
                <Input 
                  value={form.nome} 
                  onChange={v => set("nome", v)} 
                  placeholder="Inserisci nome camera" 
                />
              </Field>
              <Field label="Codice">
                <Input 
                  value={form.codice} 
                  onChange={v => set("codice", v)} 
                  placeholder="Inserisci codice camera" 
                />
              </Field>
            </div>

            {/* Tipo e Piano */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Tipo Camera">
                <Select 
                  value={form.tipo} 
                  onChange={v => set("tipo", v)} 
                  options={TIPI_CAMERA} 
                />
              </Field>
              <Field label="Piano">
                <Input 
                  value={form.piano} 
                  onChange={v => set("piano", v)} 
                  placeholder="Es: 1° piano, 2° piano" 
                />
              </Field>
            </div>

            {/* Posti e Stato */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Posti Letto">
                <Input 
                  type="number"
                  value={form.posti} 
                  onChange={v => set("posti", v)} 
                  placeholder="Numero posti letto" 
                />
              </Field>
              <Field label="Stato">
                <Select 
                  value={form.stato} 
                  onChange={v => set("stato", v)} 
                  options={STATI_CAMERA} 
                />
              </Field>
            </div>

            {/* Prezzi */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Prezzo Base (€)">
                <Input 
                  type="number"
                  value={form.prezzo_base} 
                  onChange={v => set("prezzo_base", v)} 
                  placeholder="Prezzo base per notte" 
                />
              </Field>
              <Field label="Prezzo Weekend (€)">
                <Input 
                  type="number"
                  value={form.prezzo_weekend} 
                  onChange={v => set("prezzo_weekend", v)} 
                  placeholder="Prezzo weekend per notte" 
                />
              </Field>
            </div>

            {/* Note */}
            <Field label="Note">
              <Textarea 
                value={form.note} 
                onChange={v => set("note", v)} 
                rows={4}
                placeholder="Inserisci note aggiuntive sulla camera" 
              />
            </Field>

            {/* Error Message */}
            {saveError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-sm">
                <AlertCircle className="h-4 w-4" /> {saveError}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-4 border-t border-border bg-muted/30">
            <div className="flex gap-3">
              <button 
                onClick={handleBack}
                className="flex-1 py-3 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
                style={{ minHeight: "44px" }}
              >
                Annulla
              </button>
              <button 
                onClick={handleSave}
                disabled={saveStatus === "saving" || !form.nome.trim()}
                className="flex-1 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ minHeight: "44px" }}
              >
                {saveStatus === "saving" ? (
                  "Salvataggio..."
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isNew ? "Crea" : "Salva"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
